import { AlertTriangle, ChevronRight, Info, OctagonAlert } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type {
  FinanceAlert,
  FinanceAlertKind,
  FinanceAlertSeverity,
} from "../../providers/commons/financeAlerts";
import { useFormatMoney } from "../useFormatMoney";

/** List each alert opens, already filtered */
const ALERT_LINKS: Record<FinanceAlertKind, string> = {
  overdue: "/finance/charges?view=overdue",
  due_soon: "/finance/charges?view=due_soon",
  not_generated: "/finance/charges?view=this_month",
  not_sent: "/finance/charges?view=not_sent",
  paid_without_invoice: "/finance/charges?view=no_invoice",
  expenses_due: "/finance/expenses?view=due_soon",
  adjustment_due: "/finance/contracts",
};

const ICONS: Record<FinanceAlertSeverity, typeof Info> = {
  error: OctagonAlert,
  warning: AlertTriangle,
  info: Info,
};

/** What needs action now: overdue, due soon, not generated, not sent, no NF... */
export const FinanceAlertsPanel = ({ alerts }: { alerts: FinanceAlert[] }) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  return (
    <Card className="gap-2 py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm font-semibold">
          {translate("crm.finance.alerts.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2">
        {alerts.length === 0 ? (
          <p className="px-2 py-4 text-sm text-muted-foreground">
            {translate("crm.finance.alerts.none")}
          </p>
        ) : (
          <ul className="flex flex-col">
            {alerts.map((alert) => {
              const Icon = ICONS[alert.severity];
              return (
                <li key={alert.kind}>
                  <Link
                    to={ALERT_LINKS[alert.kind]}
                    className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-accent"
                  >
                    <Icon
                      className={cn(
                        "size-4 shrink-0",
                        alert.severity === "error"
                          ? "text-destructive"
                          : "text-muted-foreground",
                      )}
                      aria-label={translate(
                        `crm.finance.alerts.severities.${alert.severity}`,
                      )}
                    />
                    <span className="flex-1">
                      {translate(`crm.finance.alerts.kinds.${alert.kind}`, {
                        smart_count: alert.count,
                      })}
                    </span>
                    {alert.amount != null && (
                      <span className="font-medium tabular-nums">
                        {formatMoney(alert.amount)}
                      </span>
                    )}
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
